import {
  OktonConfirmDialog,
  OktonDateFilter,
  OktonLoadingCard,
  OktonErrorCard,
  OktonSearchSelect,
  OktonPageHeader,
  OktonSearchFilter,
  OktonSegmentedFilter,
  ColunaGrid,
  useGrid,
  OktonDataGrid,
  TAMANHOS_PAGINA,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  useToast,
  Badge,
  Box,
  Button,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { ArrowLeftRight, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import BankAppPage from '../../layout/BankAppPage';
import { useContasBancarias } from '../../services/hooks/useContasBancarias';
import {
  pontaDaConta,
  Transferencia,
  useDeleteTransferencia,
  useTransferencias,
} from '../../services/hooks/useTransferencias';
import { formatCustomDate } from '../../utils/formatCustomDate';
import formatValue from '../../utils/formatValue';
import { FiltroData, setaFiltroAnual } from '../../utils/setaFiltroDiario';
import { colunasDataGrid } from '../../utils/colunasDataGrid';

// Pontas conciliadas de uma Transferência: cada ponta bate com o extrato da
// sua Conta, a saída na origem e a entrada no destino.
// Como a ponta se identifica na tela: a Conta quando o vínculo existe, a
// Moeda quando não — é ela que a Transferência guarda de verdade.
function ponta(conta: string | null, moeda: string | null): string {
  return conta ?? moeda ?? '?';
}

function pontasConciliadas(t: Transferencia): number {
  return (
    Number(t.conciliadoorigem_tba !== '0') +
    Number(t.conciliadodestino_tba !== '0')
  );
}

const COLUNAS: ColunaGrid<Transferencia>[] = [
  { chave: 'data_tba', titulo: 'Data', valor: t => t.data_tba, tipo: 'data' },
  {
    chave: 'documento_tba',
    titulo: 'Referência',
    valor: t => t.documento_tba,
  },
  {
    chave: 'origem',
    titulo: 'Origem → Destino',
    // As duas pontas num campo só: é assim que a linha mostra, e quem busca
    // por uma das contas acha a transferência pelos dois lados.
    valor: t =>
      `${ponta(t.origemdescricao_tba, t.origemmoeda_tba)} → ${ponta(
        t.destinodescricao_tba,
        t.destinomoeda_tba,
      )}`,
  },
  {
    chave: 'moedabanco',
    titulo: 'Moeda Banco',
    valor: t => `${t.origemmoeda_tba ?? '?'} → ${t.destinomoeda_tba ?? '?'}`,
  },
  { chave: 'nome_pct', titulo: 'Plano de Contas', valor: t => t.nome_pct },
  {
    chave: 'conciliacao',
    titulo: 'Conciliação',
    valor: t => pontasConciliadas(t),
    tipo: 'numero',
  },
  {
    chave: 'valor_tba',
    titulo: 'Valor',
    valor: t => Number(t.valor_tba),
    tipo: 'numero',
    alinhamento: 'right',
  },
];

const Transferencias = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');

  const [filtroData, setFiltroData] = useState<FiltroData>(setaFiltroAnual());
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState<
    'all' | 'pending' | 'reconciled'
  >('all');

  const { data: contas = [] } = useContasBancarias();
  const {
    data: transferencias = [],
    isLoading,
    isError,
    refetch,
  } = useTransferencias(
    filterAccount || undefined,
    'all',
    filtroData[1],
    filtroData[2],
  );

  // Sem Conta escolhida, pendente e a Transferencia com alguma ponta por
  // conciliar; com Conta, so a ponta daquela Conta importa.
  const filtradas = transferencias.filter(t => {
    if (filterStatus === 'all') return true;
    const ponta = filterAccount ? pontaDaConta(t, filterAccount) : null;
    const conciliada = ponta
      ? ponta.conciliada
      : t.conciliadoorigem_tba !== '0' && t.conciliadodestino_tba !== '0';
    return filterStatus === 'reconciled' ? conciliada : !conciliada;
  });
  const deleteMutation = useDeleteTransferencia();

  const toast = useToast();
  const [itemExcluir, setItemExcluir] = useState<string | null>(null);
  const [excluindo, setExcluindo] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const {
    ordenacao,
    alternarOrdenacao,
    itens: visiveis,
    filtrados,
    pagina,
    setPagina,
    tamanhoPagina,
    setTamanhoPagina,
  } = useGrid(filtradas, COLUNAS, busca);

  const remove = async () => {
    if (!itemExcluir) return;
    setExcluindo(itemExcluir);
    try {
      await deleteMutation.mutateAsync(itemExcluir);
      setItemExcluir(null);
      refetch();
    } catch (err) {
      toast({
        title:
          err instanceof Error
            ? err.message
            : 'Erro ao excluir a Transferência.',
        status: 'error',
        position: 'bottom-right',
        isClosable: true,
      });
    } finally {
      setExcluindo(null);
    }
  };

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Transferências Bancárias"
        subtitle="Movimentação entre contas próprias"
        actions={
          <>
            <OktonDateFilter
              value={{
                preset: filtroData[0],
                start: filtroData[1],
                end: filtroData[2],
              }}
              onChange={next =>
                setFiltroData([next.preset, next.start, next.end])
              }
            />
          </>
        }
      />

      <Flex minH="0" w="100%" my="3" mt="4" gridGap={2} flexWrap="wrap">
        <OktonSegmentedFilter<typeof filterStatus>
          value={filterStatus}
          onChange={setFilterStatus}
          options={[
            { value: 'all', label: 'Todos' },
            { value: 'pending', label: 'Pendentes' },
            { value: 'reconciled', label: 'Conciliados' },
          ]}
        />
        <OktonSearchFilter
          value={busca}
          onChange={setBusca}
          placeholder="Buscar por conta, referência, plano..."
        />
        <Flex
          borderWidth="1px"
          ml="auto"
          p="2"
          rounded="2xl"
          bg={bg}
          align="center"
          gridGap={2}
        >
          <Button
            leftIcon={<Plus size={18} />}
            colorScheme="blue"
            onClick={() => history.push('/transferencias/nova')}
            minW="190px"
          >
            Nova Transferência
          </Button>
          <OktonSearchSelect
            name="filtroconta"
            placeholder="Todas as contas"
            value={filterAccount}
            onChange={valor => setFilterAccount(valor)}
            options={[
              ...contas.map(c => ({
                value: c.id_cts,
                label: c.descricao_cts,
              })),
            ]}
          />
        </Flex>
      </Flex>

      {isLoading ? (
        <Flex mt="1">
          <OktonLoadingCard h="200px" />
        </Flex>
      ) : isError ? (
        <Flex mt="1">
          <OktonErrorCard h="200px" onRetry={refetch} />
        </Flex>
      ) : (
        <Box bg={bg} mt="1" rounded="2xl">
          {filtrados.length === 0 ? (
            <OktonEmptyState
              icon={ArrowLeftRight}
              title={
                busca
                  ? 'Nada encontrado para a busca'
                  : 'Nenhuma transferência no período'
              }
            />
          ) : (
            <OktonDataGrid
              rows={visiveis}
              columns={[
                ...colunasDataGrid(COLUNAS, ordenacao, alternarOrdenacao, {
                  data_tba: t => (
                    <Box color="gray.600" whiteSpace="nowrap">
                      {formatCustomDate(
                        t.data_tba.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                    </Box>
                  ),
                  documento_tba: t => (
                    <Box color={color}>{t.documento_tba ?? '—'}</Box>
                  ),
                  origem: t => (
                    <Box color={color} fontWeight="bold">
                      {ponta(t.origemdescricao_tba, t.origemmoeda_tba)} →{' '}
                      {ponta(t.destinodescricao_tba, t.destinomoeda_tba)}
                    </Box>
                  ),
                  moedabanco: t => (
                    <Box color="gray.500">
                      {t.origemmoeda_tba ?? '?'} → {t.destinomoeda_tba ?? '?'}
                    </Box>
                  ),
                  nome_pct: t => (
                    <>
                      {t.nome_pct ? (
                        <Badge colorScheme="blue" borderRadius="full" px="2">
                          {t.nome_pct}
                        </Badge>
                      ) : (
                        <Text color="gray.400">—</Text>
                      )}
                    </>
                  ),
                  conciliacao: t => {
                    const pontas = pontasConciliadas(t);
                    return (
                      <>
                        <Badge
                          colorScheme={
                            pontas === 2
                              ? 'green'
                              : pontas === 1
                              ? 'blue'
                              : 'orange'
                          }
                          borderRadius="full"
                          px="2"
                        >
                          {pontas} de 2
                        </Badge>
                      </>
                    );
                  },
                  valor_tba: t => (
                    <Box
                      fontWeight="semibold"
                      whiteSpace="nowrap"
                      color={color}
                    >
                      {formatValue(Number(t.valor_tba))}
                    </Box>
                  ),
                }),
                {
                  key: 'acoes',
                  header: null,
                  align: 'right',
                  render: t => {
                    const pontas = pontasConciliadas(t);
                    return (
                      <Flex justify="flex-end" gridGap="1">
                        {/* Conciliada não se exclui: o back recusa, e o botão
                        só prometeria uma ação que vai falhar. */}
                        {pontas === 0 && (
                          <IconButton
                            aria-label="Excluir"
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            icon={<Trash2 size={16} />}
                            onClick={() => setItemExcluir(t.id_tba)}
                          />
                        )}
                      </Flex>
                    );
                  },
                },
              ]}
              getRowId={t => t.id_tba}
              page={pagina + 1}
              pageSize={tamanhoPagina}
              total={filtrados.length}
              pageSizeOptions={TAMANHOS_PAGINA}
              onPageChange={nova => setPagina(nova - 1)}
              onPageSizeChange={setTamanhoPagina}
            />
          )}
        </Box>
      )}
      <OktonConfirmDialog
        isOpen={Boolean(itemExcluir)}
        onClose={() => {
          if (!excluindo) setItemExcluir(null);
        }}
        onConfirm={remove}
        title="Confirmar exclusão"
        description="Excluir esta Transferência do IFLEX?"
        confirmLabel="Excluir"
        intent="danger"
        isLoading={Boolean(excluindo)}
      />
    </BankAppPage>
  );
};

export default Transferencias;
